import * as React from 'react';
import { PropsWithChildren, useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { cn, customImageLoader, formatDateToDayMonthYear } from '@restworld/utils-common';
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Badge, Heading } from '@restworld/ui-ds-base';
import { ImagePlaceholder } from '@restworld/ui-components';

export function EmployerSpontaneousCandidateCard({
  candidate,
  restaurantCount,
  restaurant,
  appliedDate,
  isHidden,
  onClick,
  isNewCandidate,
}: EmployerSpontaneousCandidateCardProps) {
  const hasMultipleRestaurants = restaurantCount > 1;

  return (
    <CardWrappper onClick={onClick}>
      <CardHeader
        appliedDate={appliedDate}
        firstName={candidate?.firstName}
        lastName={candidate?.lastName}
        isHidden={isHidden}
        isNewCandidate={isNewCandidate}
      />
      <ContentWrapper>
        <CardInfoSection
          candidateDistanceToRestaurant={candidate?.distanceToRestaurant}
          candidateEmail={candidate?.email}
          candidateImage={candidate?.image}
          candidatePhone={candidate?.phone}
        />
        {hasMultipleRestaurants && (
          <CardAppliedToSection
            restaurantAddress={restaurant?.address}
            restaurantLogo={restaurant.logo}
            restaurantName={restaurant?.name}
          />
        )}
      </ContentWrapper>
    </CardWrappper>
  );
}
export default EmployerSpontaneousCandidateCard;

function CardWrappper({ children, onClick }: PropsWithChildren<{ onClick?: () => void }>) {
  return <StyledButton onClick={onClick}>{children}</StyledButton>;
}

function CardHeader({ firstName, lastName, isHidden, appliedDate, isNewCandidate }: CardHeaderProps) {
  const { t } = useTranslation('common');

  const formattedDate = useMemo(() => formatDateToDayMonthYear(appliedDate), [appliedDate]);

  return (
    <CardHeaderWrapper>
      <Heading as="span" size={5} weight="semibold" color="neutral" className="text-lg">
        {`${firstName} ${lastName}`}
      </Heading>
      <BadgesWrapper>
        {isHidden && <Badge variant="disabled">{t('discarded')}</Badge>}
        {isNewCandidate && (
          <Badge variant="warning" className="capitalize">
            {t('new')}
          </Badge>
        )}
        <Badge variant="neutral">{`${t('applied_on')} ${formattedDate}`}</Badge>
      </BadgesWrapper>
    </CardHeaderWrapper>
  );
}

function CardInfoSection({
  candidateDistanceToRestaurant,
  candidateEmail,
  candidateImage,
  candidatePhone,
}: CardInfoSectionProps) {
  return (
    <InfoSectionWrapper>
      <CandidateImageWrapper>
        {candidateImage ? (
          <Image
            src={candidateImage || ''}
            alt=""
            fill
            loader={customImageLoader}
            className="object-cover object-center"
          />
        ) : (
          <ImagePlaceholder className="w-24 h-24" />
        )}
      </CandidateImageWrapper>
      <CandidateInfoWrapper>
        <span className="flex items-center gap-1 text-sm text-neutral-200 font-medium text-left">
          <MapPinIcon className="h-4 w-4 text-neutral-200 shrink-0" />
          {candidateDistanceToRestaurant}
        </span>
        <span className="flex items-center gap-1 text-sm text-neutral-200 font-medium text-left">
          <PhoneIcon className="h-4 w-4 text-neutral-200 shrink-0" />
          {candidatePhone}
        </span>
        <span className="flex items-center gap-1 text-sm text-neutral-200 font-medium text-left w-full max-w-[165px] whitespace-nowrap truncate lg:max-w-[184px]">
          <EnvelopeIcon className="h-4 w-4 text-neutral-200 shrink-0" />
          {candidateEmail}
        </span>
      </CandidateInfoWrapper>
    </InfoSectionWrapper>
  );
}

function CardAppliedToSection({ restaurantAddress, restaurantLogo, restaurantName }: CardAppliedToSectionProps) {
  const { t } = useTranslation('common');

  return (
    <AppliedToSectionWrapper>
      <Heading as="span" size={6} weight="medium" color="neutral-light" className="text-sm">
        {t('spontaneously_applied_to')}:
      </Heading>
      <AppliedToContentWrapper>
        <LogoWrapper>
          {restaurantLogo ? (
            <Image src={restaurantLogo} alt="" fill className="object-cover object-center" loader={customImageLoader} />
          ) : (
            <ImagePlaceholder className="w-fit h-fit" iconClasses="h-7 w-7 fill-purple-100" />
          )}
        </LogoWrapper>
        <RestaurantInfoWrapper>
          <Heading as="span" size={6} weight="semibold" color="neutral" className="text-sm text-left">
            {restaurantName}
          </Heading>
          <Heading as="span" size={6} weight="normal" color="neutral" className="text-sm text-left truncate max-w-full">
            {restaurantAddress}
          </Heading>
        </RestaurantInfoWrapper>
      </AppliedToContentWrapper>
    </AppliedToSectionWrapper>
  );
}

export interface EmployerSpontaneousCandidateCardProps {
  candidate: {
    firstName: string;
    lastName: string;
    distanceToRestaurant: string;
    phone: string;
    email: string;
    image: string;
  };
  restaurant: {
    name: string;
    address: string;
    logo: string;
  };
  appliedDate: string;
  isHidden: boolean;
  isNewCandidate?: boolean;
  restaurantCount: number;
  onClick?: () => void;
}
interface CardHeaderProps {
  firstName?: string;
  lastName?: string;
  isHidden?: boolean;
  appliedDate?: string;
  isNewCandidate?: boolean;
}
interface CardInfoSectionProps {
  candidateDistanceToRestaurant?: string;
  candidatePhone?: string;
  candidateEmail?: string;
  candidateImage?: string;
}
interface CardAppliedToSectionProps {
  restaurantName?: string;
  restaurantAddress?: string;
  restaurantLogo?: string;
}

const StyledButton = ({ className, ...props }: any) => (
  <button
    className={cn(
      'appearance-none block p-4 border border-neutral-40 rounded-2xl bg-neutral-10 shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)] w-full hover:border-purple-500 hover:shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)]',
      className
    )}
    {...props}
  />
);
const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col lg:flex-row', className)} {...props} />
);
const CardHeaderWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col justify-between mb-3 lg:flex-row', className)} {...props} />
);
const BadgesWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-3 mt-3 lg:mt-0', className)} {...props} />
);
const InfoSectionWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-3 lg:pr-6 lg:w-[335px] lg:min-w-[335px] lg:max-w-[335px]', className)} {...props} />
);
const CandidateInfoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col justify-center gap-3', className)} {...props} />
);
const AppliedToSectionWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border-neutral-40 border-t w-full mt-3 pt-3 lg:border-l lg:pl-6 lg:border-t-0 lg:mt-0 lg:pt-0',
      className
    )}
    {...props}
  />
);
const AppliedToContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('bg-neutral-30 p-4 w-full flex flex-col gap-2 rounded-lg mt-3 lg:flex-row lg:gap-4', className)}
    {...props}
  />
);
const RestaurantInfoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-1', className)} {...props} />
);
const CandidateImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'rounded-lg relative w-24 h-24 min-w-[6rem] flex items-center justify-center object-cover object-center overflow-hidden',
      className
    )}
    {...props}
  />
);
const LogoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border border-neutral-40 rounded-lg relative w-10 h-10 min-w-[2.5rem] flex items-center justify-center object-cover object-center overflow-hidden bg-white',
      className
    )}
    {...props}
  />
);
